import type {
  DomainGuid,
  EvaluatedStatus
} from '@/features/execute-calculation-set/overmind/calculation';

import type { RenderedElementType } from './MapElementDesigner';
import { MapElementDesigner } from './MapElementDesigner';

export type ColorStyle = {
  weight: number;
  radius?: number;
  color: string;
  fillColor: string;
  fillOpacity: number;
  className?: string;
};

export type ColorStyles = {
  selectedPoint: ColorStyle;
  selectedLine: ColorStyle;
  selectedArea: ColorStyle;
  subnetPointLine: ColorStyle;
  subnetArea: ColorStyle;
};

export type HighlightedElements = Record<DomainGuid, EvaluatedStatus>;

export type IsHighlighted = {
  type: 'SOME_HIGHLIGHTED';
  context: { highlighted: HighlightedElements };
};
export type NotHighlighted = { type: 'NONE_HIGHLIGHTED' };

export type HighlightStates = IsHighlighted | NotHighlighted;

export type StyleMapElementFunction = (
  renderedElement: RenderedElementType,
  selectedItemInMap: DomainGuid | undefined,
  initatingElement: DomainGuid,
  highlightState: HighlightStates,
  colors: ColorStyles,
  externalNetworkEntryPoint?: DomainGuid | undefined
) => ColorStyle | undefined;

export const styleMapElementForLvShortCircuit = (shouldUseInitiatingObjectHighlighting: boolean) => (
  renderedElement: RenderedElementType,
  selectedItemInMap: DomainGuid | undefined,
  initiatingElement: DomainGuid,
  highlightState: HighlightStates,
  colors: ColorStyles,
): ColorStyle | undefined => {
  const designer = MapElementDesigner.createForLvShortCircuit(
    renderedElement,
    selectedItemInMap,
    initiatingElement,
    highlightState,
    colors,
    shouldUseInitiatingObjectHighlighting
  );

  switch (renderedElement.type) {
    case 'Point':
      return designer.stylePoint();
    case 'Polygon':
      return designer.stylePolygon();
    case 'LineString':
      return designer.styleLineString();
  }
};

export const styleMapElementForAllCalculations: (shouldUseInitiatingObjectHighlighting: boolean) => StyleMapElementFunction = 
(shouldUseInitiatingObjectHighlighting: boolean) =>
(
  renderedElement: RenderedElementType,
  selectedItemInMap: DomainGuid | undefined,
  initatingElement: DomainGuid,
  highlightState: HighlightStates,
  colors: ColorStyles,
  externalNetworkEntryPoint?: DomainGuid | undefined
): ColorStyle | undefined => {
  const designer = MapElementDesigner.create(
    renderedElement,
    selectedItemInMap,
    initatingElement,
    highlightState,
    colors,
    externalNetworkEntryPoint,
    shouldUseInitiatingObjectHighlighting
  );

  switch (renderedElement.type) {
    case 'Point':
      return designer.stylePoint();
    case 'Polygon':
      return designer.stylePolygon();
    case 'LineString':
      return designer.styleLineString();
  }
};
